/**
 * Source-repository activity for an application.
 *
 * Relay knew what every application *was* and whether it was up, but nothing
 * about whether anyone was still working on it. On a public build log that is
 * the most interesting fact there is: a reader wants to see what is being
 * actively built, not an alphabetical list where a finished experiment sits
 * above the thing shipped this morning.
 */
export interface RepositoryActivity {
  /** `owner/name`, the cache key and the thing two apps can legitimately share. */
  slug: string
  url: string
  defaultBranch?: string

  /** Commits on the default branch, all time. */
  totalCommits?: number
  /** Commits on the default branch in the trailing 30 days. The ranking signal. */
  commits30d?: number
  /** Commits in the trailing 7 days, for "shipped this week". */
  commits7d?: number

  /**
   * Commits per day for the trailing 30 days, oldest first, always length 30.
   *
   * Fixed length so the UI can render a strip without deciding what a missing
   * day means, and so two repositories are always visually comparable.
   */
  dailyCommits?: number[]

  lastCommitAt?: string
  lastCommitSha?: string
  lastCommitMessage?: string

  /** Pushes to any branch in the trailing 7 days, with the commits each carried. */
  recentPushes?: Array<{ at: string; ref: string; commits: number; head: string }>

  /** When Relay last successfully read this repository. */
  fetchedAt?: string
  status: 'available' | 'unavailable' | 'unauthorized' | 'stale'
  message?: string
}

/**
 * Parses `owner/name` out of a repository URL.
 *
 * Returns undefined for anything that is not a GitHub repository, so a future
 * GitLab or Azure DevOps entry degrades to "no activity" instead of producing
 * a nonsense slug that then 404s on every refresh.
 */
export function parseGitHubSlug(repositoryUrl: string): string | undefined {
  let url: URL
  try {
    url = new URL(repositoryUrl)
  } catch {
    return undefined
  }
  if (url.hostname.toLowerCase() !== 'github.com') return undefined

  const segments = url.pathname.split('/').filter(Boolean)
  if (segments.length < 2) return undefined
  const owner = segments[0]
  const name = segments[1].replace(/\.git$/i, '')
  if (!owner || !name) return undefined
  return `${owner}/${name}`
}

/**
 * Everything Relay knows about when an application was last worked on.
 *
 * A commit and a deploy are both evidence of work, and neither one alone is
 * enough. A repository can go quiet while the thing keeps shipping from a
 * release branch, and an application can have no repository Relay can read at
 * all and still have gone to production this morning.
 */
export interface ActivitySignals {
  activity?: RepositoryActivity
  /** When this application was last deployed to production, if known. */
  deployedAt?: string
}

function parsedMs(value: string | undefined): number {
  if (!value) return 0
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * The most recent moment an application was demonstrably touched.
 *
 * Zero means "no evidence either way", which sorts last — deliberately not
 * the epoch, so an unparseable timestamp degrades to "unknown" instead of
 * ranking as 1970 and dragging a live project to the bottom of the page.
 */
export function lastTouchedMs(signals: ActivitySignals): number {
  return Math.max(
    parsedMs(signals.activity?.lastCommitAt),
    parsedMs(signals.deployedAt),
  )
}

/**
 * How fast a commit stops counting as "what I am working on now", in days.
 *
 * Three days. Shah commits daily across twenty repositories, so the question
 * the front page answers is what happened this week, not this month: at this
 * half-life a commit from three days ago is worth half of one made today and
 * one from a fortnight ago is worth about 4%. Ten days was too slow — it let a
 * burst that had already stopped sit above the thing being built right now.
 */
export const MOMENTUM_HALF_LIFE_DAYS = 3

/**
 * The most one day of commits can contribute, before recency weighting.
 *
 * A 196-commit day and a 20-commit day are both one day of work. Without a cap
 * a single import, merge or generated-file sweep outranks a fortnight of
 * steady building, which is the opposite of measuring focus — and it really
 * happened: one 196-commit day carried a project that had then gone quiet for
 * nine days into the featured row.
 */
export const DAILY_COMMIT_CAP = 20

/**
 * How much it is worth that a project's whole life is happening right now.
 *
 * This is the correction for the structural advantage age gives. An older
 * repository accumulates commits by existing, so ranking on volume alone
 * permanently favours whatever has been around longest — exactly backwards for
 * a page that is supposed to show what is being worked on. `focus` is the share
 * of a project's lifetime commits that landed in the last seven days: about
 * 0.11 for a two-year-old product having a busy week, 1.0 for something started
 * on Tuesday.
 *
 * At weight 1 a project where everything is happening now counts double.
 *
 * It multiplies recent work rather than standing alone, which is the property
 * that keeps it honest: a dormant repository with three lifetime commits has a
 * focus of 0 because none of them are recent, so this can never float dead
 * things to the top — it can only reorder projects that are already active.
 */
export const FOCUS_WEIGHT = 1

/** Mean decay weight across a range of day-ages, for the no-daily-data fallback. */
function meanDecay(fromAgeDays: number, toAgeDays: number): number {
  let total = 0
  let days = 0
  for (let age = fromAgeDays; age <= toAgeDays; age += 1) {
    total += 0.5 ** (age / MOMENTUM_HALF_LIFE_DAYS)
    days += 1
  }
  return days === 0 ? 0 : total / days
}

/**
 * Recent work: days of commits, capped per day and weighted by how recent.
 *
 * Uses the per-day buckets when they exist, because "forty commits on one day
 * three weeks ago" and "forty spread over this week" are different amounts of
 * current focus and the monthly total cannot tell them apart. Falls back to the
 * 7- and 30-day counts at their average weights when a repository has no daily
 * breakdown, so an app is never ranked at zero merely because GitHub's stats
 * endpoint was still computing.
 */
export function recentWork(activity: RepositoryActivity | undefined): number {
  if (!activity) return 0
  const daily = activity.dailyCommits
  if (daily?.length) {
    // Oldest first, so the last element is today.
    const newest = daily.length - 1
    return daily.reduce(
      (total, commits, index) =>
        total +
        Math.min(commits, DAILY_COMMIT_CAP) *
          0.5 ** ((newest - index) / MOMENTUM_HALF_LIFE_DAYS),
      0,
    )
  }
  const week = activity.commits7d ?? 0
  const month = activity.commits30d ?? 0
  // No daily shape to cap, so cap the weekly figure at a week of full days.
  const cappedWeek = Math.min(week, DAILY_COMMIT_CAP * 7)
  const cappedRest = Math.min(Math.max(month - week, 0), DAILY_COMMIT_CAP * 23)
  return cappedWeek * meanDecay(0, 6) + cappedRest * meanDecay(7, 29)
}

/**
 * What share of everything ever committed to a project landed this week.
 *
 * Zero when nothing is known, so an application with no repository is ranked on
 * its recent work alone rather than being handed a multiplier it did not earn.
 */
export function focusShare(activity: RepositoryActivity | undefined): number {
  const total = activity?.totalCommits ?? 0
  const week = activity?.commits7d ?? 0
  if (total <= 0 || week <= 0) return 0
  return Math.min(1, week / total)
}

/**
 * How much of a project's life must be happening now for it to count as new.
 *
 * 0.8 — four commits in five, ever, landed this week. A project started on
 * Tuesday sits at 1.0 and falls out of this band by itself as it accumulates
 * history, which is the property that matters: "new" expires on its own and
 * nothing has to remember to un-flag it.
 */
export const NEW_PROJECT_FOCUS = 0.8

/**
 * How much recent work a new project needs before it leads the page.
 *
 * Twelve is about a full capped day of commits at today's weight, or two solid
 * days three days ago. Below it a young repository is a scratch idea rather
 * than something under heavy development, and a bar any lower fills the
 * featured row with four-commit stubs — at 6 this fleet put a waitlist page and
 * a half-migrated tool ahead of the products actually being built.
 */
export const BREAKOUT_WORK_THRESHOLD = 12

/**
 * A new project under heavy development, which leads the page ahead of
 * everything else.
 *
 * The one place the ranking is a tier rather than a score. Something new
 * absorbing this much attention is the most interesting thing a build log can
 * show, and on a pure score it loses to any mature product having a busy week —
 * not because less is going into it, but because it has had less time to
 * accumulate. Both conditions are required: new without the work is a stub, and
 * the work without newness is just an ordinary busy week, already ranked by the
 * score.
 *
 * It is self-clearing from both sides. The burst stops and the work decays
 * below the threshold; the project matures and its focus share falls below the
 * band.
 */
export function isBreakout(activity: RepositoryActivity | undefined): boolean {
  return (
    focusShare(activity) >= NEW_PROJECT_FOCUS &&
    recentWork(activity) >= BREAKOUT_WORK_THRESHOLD
  )
}

/**
 * How much focus and effort an application currently represents, as one number.
 *
 *     score = recent work × (1 + focus)
 *
 * Recent work is capped-per-day, recency-weighted commits. Focus is the share
 * of the project's entire history happening this week. Lifetime volume is
 * deliberately *not* a term of its own: an older repository accumulates commits
 * by existing, and rewarding that put a product with 779 commits and fourteen
 * this month above things being actively built.
 *
 * A deploy counts as a touch for the tiebreak but contributes no score — it is
 * evidence that something shipped, not that work went into it. With no commits
 * at all the score is 0 and the curated order decides.
 */
export function effortScore(signals: ActivitySignals): number {
  const work = recentWork(signals.activity)
  if (work <= 0) return 0
  return work * (1 + FOCUS_WEIGHT * focusShare(signals.activity))
}

/**
 * Ranks applications for the public build log by where the work is going.
 *
 * The effort score leads, the last touch breaks a tie, and anything with no
 * commit and no deploy keeps its curated order below the active work rather
 * than being scattered through it.
 */
export function activityRank(signals: ActivitySignals): {
  effort: number
  lastTouchedMs: number
} {
  return {
    effort: effortScore(signals),
    lastTouchedMs: lastTouchedMs(signals),
  }
}

/**
 * Buckets a day's commit count into an intensity step for the activity strip.
 *
 * Fixed thresholds rather than a scale normalised per repository: the point of
 * putting these side by side is that a dense strip means more work than a
 * sparse one. Normalising per repository would make a quiet project with one
 * busy day look exactly like a project shipping daily.
 */
export function commitIntensity(commits: number): 0 | 1 | 2 | 3 | 4 {
  if (commits <= 0) return 0
  if (commits <= 2) return 1
  if (commits <= 5) return 2
  if (commits <= 10) return 3
  return 4
}

export function compareByActivity(
  left: ActivitySignals,
  right: ActivitySignals,
): number {
  // A new project under heavy development leads, ahead of the score. See
  // `isBreakout`. Two of them are ordered against each other by the score like
  // anything else.
  const breakout = Number(isBreakout(right.activity)) - Number(isBreakout(left.activity))
  if (breakout !== 0) return breakout

  const a = activityRank(left)
  const b = activityRank(right)
  // Scores are floating point; a hair's difference is not a real ordering, so
  // near-equal efforts fall through to the date rather than to rounding noise.
  if (Math.abs(a.effort - b.effort) > 1e-6) return b.effort - a.effort
  if (a.lastTouchedMs !== b.lastTouchedMs) return b.lastTouchedMs - a.lastTouchedMs
  return 0
}
